import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchLinesAtStop, buildStopLineTimetable } from '../services/timetableAPI';
import { getTodayDateStr, sortRoutesByNumber } from '../utils/timetable';

function StopTimetable({ stopId }) {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  const currentHour = new Date().getHours();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchLinesAtStop(stopId);
        if (!cancelled) {
          setLines(sortRoutesByNumber(data));
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Błąd pobierania linii na przystanku:', err);
          setError('Nie udało się pobrać listy linii');
          setLoading(false);
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [stopId]);

  // Załaduj rozkład po wybraniu linii
  useEffect(() => {
    if (!selectedRoute) {
      setSchedule(null);
      return;
    }
    let cancelled = false;
    async function load() {
      try {
        setScheduleLoading(true);
        const dateStr = getTodayDateStr();
        const data = await buildStopLineTimetable(stopId, selectedRoute.routeId, dateStr);
        if (!cancelled) {
          setSchedule(data);
          setScheduleLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Błąd pobierania rozkładu:', err);
          setScheduleLoading(false);
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [stopId, selectedRoute]);

  if (loading) return <div className="loading">Ładowanie rozkładu jazdy...</div>;
  if (error) return <div className="error">{error}</div>;
  if (lines.length === 0) return <div className="no-departures">Brak danych rozkładowych dla tego przystanku.</div>;

  return (
    <div className="stop-timetable">
      {/* Lista linii */}
      {!selectedRoute && (
        <div className="stop-timetable-lines">
          {lines.map((line) => {
            const isTram = line.routeType === 'TRAM';
            return (
              <button
                key={line.routeId}
                className={`stop-timetable-line-btn ${isTram ? 'stop-timetable-line-btn--tram' : ''}`}
                onClick={() => setSelectedRoute(line)}
              >
                <span className={`line-badge ${isTram ? 'line-badge--tram' : ''}`}>
                  {line.routeShortName}
                </span>
                <span className="stop-timetable-line-name">{line.routeLongName}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Rozkład wybranej linii */}
      {selectedRoute && (
        <div className="stop-timetable-detail">
          <button
            className="stop-timetable-back"
            onClick={() => setSelectedRoute(null)}
          >
            ← Wszystkie linie
          </button>

          <div className="stop-timetable-detail-header">
            <span className={`line-badge ${selectedRoute.routeType === 'TRAM' ? 'line-badge--tram' : ''}`}>
              {selectedRoute.routeShortName}
            </span>
            <span className="stop-timetable-detail-name">{selectedRoute.routeLongName}</span>
            <Link to={`/rozklad/${selectedRoute.routeId}`} className="stop-timetable-full-link">
              Pełny rozkład
            </Link>
          </div>

          {scheduleLoading && <div className="loading">Ładowanie...</div>}

          {!scheduleLoading && schedule && schedule.hours.length === 0 && (
            <div className="no-departures">Brak odjazdów tej linii z tego przystanku.</div>
          )}

          {!scheduleLoading && schedule && schedule.hours.length > 0 && (
            <div className="stop-timetable-grid">
              {schedule.hours.map((hour) => (
                <div
                  key={hour}
                  className={`stop-timetable-hour-row ${hour === currentHour ? 'stop-timetable-hour-row--current' : ''}`}
                >
                  <div className="stop-timetable-hour">
                    {String(hour).padStart(2, '0')}
                  </div>
                  <div className="stop-timetable-minutes">
                    {schedule.minutes[hour].map((m) => (
                      <span key={m} className="stop-timetable-minute">
                        {String(m).padStart(2, '0')}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default StopTimetable;
